import { useState } from 'react';
import type { MapPOI } from 'shared/map/interfaces';
import { kilometersBetween } from 'shared/map/mapHelpers';
import { PoiTypes } from 'shared/map/pois/poiTypes';

import { HotelMap } from '@/components/hotelMap/HotelMap';
import type { BasicHotel, BasicHotelsResponse, Filters, MapBounds, MapConfig, MapState, SearchConfig } from '@/interfaces';
import { extractNameFromKey } from '@/utils/kayakKeys';
import { HotelMapCard } from '../../components/searchResultItemMapCard/HotelMapCard';
import { MapLegend } from '../../components/mapLegend/MapLegend';
import { trackSearchEdit } from '@/utils/tracking/finnAnalytics';

// TODO REIS-5230: Identical to POI_TYPES in HotelPage.tsx. Extract to common list.
const POI_TYPES: PoiTypes[] = [
    PoiTypes.AVINOR_AIRPORT,
    PoiTypes.ENTUR_AIRPORT,
    PoiTypes.ENTUR_BUS,
    PoiTypes.ENTUR_FERRY,
    PoiTypes.ENTUR_SUBWAY,
    PoiTypes.ENTUR_TRAIN,
    PoiTypes.ENTUR_TRAM,
    PoiTypes.CHARGING_STATION,
    PoiTypes.STORE,
    PoiTypes.SHOPPING_CENTRE,
    PoiTypes.PHARMACY,
    PoiTypes.KIOSK,
    PoiTypes.GAS_STATION,
    PoiTypes.SPORTS_FACILITY,
    PoiTypes.GYM,
    PoiTypes.SKI_CENTRE,
    PoiTypes.TRAVEL,
    PoiTypes.TOURIST_ATTRACTION,
    PoiTypes.MUSEUM,
    PoiTypes.HOSPITAL,
];

interface Props {
    filters: Filters;
    mapState: MapState;
    numberOfHotels: number;
    searchConfig: SearchConfig;
    setMapState: (config: MapConfig) => void;
    setMapBounds: (mapBounds: MapBounds) => void;
    triggerFetch: (resetMap: boolean) => void;
}

export const SearchResultsMap = ({ filters, mapState, numberOfHotels, searchConfig, setMapState, setMapBounds, triggerFetch }: Props) => {
    const [displayPrices, setDisplayPrices] = useState(false);

    const mapHotels = mapState.hotels.reduce(
        (hotels: BasicHotel[], response: BasicHotelsResponse): BasicHotel[] => hotels.concat(response.results),
        [],
    );
    const selectedHotel = mapHotels.find((hotel) => extractNameFromKey(hotel.key) === mapState.selectedHotel.key);
    const selectedPois: MapPOI[] = selectedHotel
        ? [
              {
                  id: extractNameFromKey(selectedHotel.key),
                  name: selectedHotel.name,
                  coordinates: [selectedHotel.longitude, selectedHotel.latitude],
                  price: displayPrices && filters.checkInDate && filters.checkOutDate ? selectedHotel.lowestRate : undefined,
              },
          ]
        : [];

    const primaryPois: MapPOI[] = mapHotels
        .filter((hotel) => extractNameFromKey(hotel.key) !== mapState.selectedHotel.key)
        .map(
            (hotel): MapPOI => ({
                id: extractNameFromKey(hotel.key),
                name: hotel.name,
                coordinates: [hotel.longitude, hotel.latitude],
                price: displayPrices && filters.checkInDate && filters.checkOutDate ? hotel.lowestRate : undefined,
            }),
        );

    const onPoiClick = (key: string) => setMapState({ selectedHotel: { key, showDetails: true } });
    const onUpdateBoundsClick = (bounds: MapBounds): void => {
        trackSearchEdit({
            position: 'map',
            checkInDate: filters.checkInDate,
            checkOutDate: filters.checkOutDate,
            rooms: filters.rooms,
            searchable: true,
            details: 'coordinate search',
        });
        setMapBounds(bounds);
        triggerFetch(true);
    };

    // NOTE: Kayak limits polygon searches to 125 miles (~200km) on each axis (north-south, east-west).
    // This measures diagonally so theoretical max is ~280km, but should use less to be on the safe side.
    const canUpdateBoundsOnClick = async (bounds: MapBounds): Promise<boolean> => {
        const size = kilometersBetween(bounds.ne, bounds.sw);
        return size <= 225;
    };

    return (
        <div className="result-list-map">
            <HotelMap
                id="result-list-map"
                selectedPois={selectedPois}
                primaryPois={primaryPois}
                poiTypes={POI_TYPES}
                className="result-list-map"
                hasClustering
                center={mapState.selectedHotel?.centerOnHotel && selectedHotel ? selectedPois[0].coordinates : undefined}
                onPinClick={onPoiClick}
                onUpdateBoundsClick={searchConfig.fixed ? undefined : onUpdateBoundsClick}
                canUpdateBoundsOnClick={searchConfig.fixed ? undefined : canUpdateBoundsOnClick}
                initialBounds={filters.mapBounds}
                initialZoom={selectedHotel ? 13 : 12}
                loading={mapState.hotels.some((response) => !response.isComplete)}>
                <MapLegend
                    checkInDate={filters.checkInDate}
                    checkOutDate={filters.checkOutDate}
                    numberOfHotels={numberOfHotels}
                    displayPrices={displayPrices}
                    setDisplayPrices={setDisplayPrices}
                    className={selectedHotel && mapState.selectedHotel?.showDetails ? 'above-map-card' : undefined}
                />
                {selectedHotel && mapState.selectedHotel?.showDetails && (
                    <div className="mapboxgl-ctrl-bottom-center">
                        <HotelMapCard
                            filters={filters}
                            closeCard={() => setMapState({ selectedHotel: {} })}
                            hotelKey={selectedHotel.key}
                            {...selectedHotel}
                        />
                    </div>
                )}
            </HotelMap>
        </div>
    );
};
